import { ChevronLeftIcon } from "../../../components/icons/ChevronLeftIcon";
import { ChevronRightIcon } from "../../../components/icons/ChevronRightIcon";

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
}: PaginationProps) {
  const getPageNumbers = () => {
    const pages: (number | "ellipsis")[] = [];

    if (totalPages <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first 5 pages
      for (let i = 1; i <= 5; i++) {
        pages.push(i);
      }
      // Show ellipsis
      pages.push("ellipsis");
      // Show last page
      pages.push(totalPages);
    }

    return pages;
  };

  const pages = getPageNumbers();

  return (
    <div className="content-stretch flex items-center justify-center relative shrink-0 w-full">
      {/* Previous Button */}
      <div className="overflow-clip relative shrink-0 size-[32px]">
        <button
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className="absolute content-stretch flex items-center justify-center left-0 min-h-[32px] rounded-[3px] top-0 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgba(5,21,36,0.06)] transition-colors"
        >
          <ChevronLeftIcon className="w-[16px] h-[16px] text-[#292A2E]" />
        </button>
      </div>

      {/* Page Numbers */}
      {pages.map((page, index) => {
        if (page === "ellipsis") {
          return (
            <div
              key={`ellipsis-${index}`}
              className="content-stretch flex h-[32px] items-center justify-center overflow-clip relative shrink-0"
            >
              <div
                className="flex flex-col font-['Noto_Sans:Regular',sans-serif] font-normal justify-center leading-[0] relative shrink-0 text-[#292a2e] text-[14px] text-center text-nowrap"
                style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
              >
                <p className="leading-[20px] whitespace-pre">...</p>
              </div>
            </div>
          );
        }

        const isActive = page === currentPage;

        return (
          <div
            key={page}
            className="content-stretch flex items-start relative shrink-0"
          >
            <button
              onClick={() => onPageChange(page)}
              className={`content-stretch flex items-center justify-center min-h-[32px] relative rounded-[3px] shrink-0 px-3 hover:bg-[rgba(5,21,36,0.06)] transition-colors ${
                isActive ? "bg-[#e9f2fe]" : ""
              }`}
            >
              <div className="content-stretch flex items-center justify-center relative shrink-0">
                <div
                  className={`flex flex-col font-['Noto_Sans:Medium',sans-serif] font-medium justify-center leading-[0] relative shrink-0 text-[14px] text-center text-nowrap ${
                    isActive ? "text-[#1868db]" : "text-[#292a2e]"
                  }`}
                  style={{ fontVariationSettings: "'CTGR' 0, 'wdth' 100" }}
                >
                  <p className="leading-[20px] whitespace-pre">{page}</p>
                </div>
              </div>
            </button>
          </div>
        );
      })}

      {/* Next Button */}
      <div className="overflow-clip relative shrink-0 size-[32px]">
        <button
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className="absolute content-stretch flex items-center justify-center left-0 min-h-[32px] rounded-[3px] top-0 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgba(5,21,36,0.06)] transition-colors"
        >
          <ChevronRightIcon className="w-[16px] h-[16px] text-[#292A2E]" />
        </button>
      </div>
    </div>
  );
}
